// Shared wiring between the consent modules and the extracted agreement assets.
//
// Each consent module.html carries one `data-<state>-<kind>-template-url`
// attribute per agreement it supports, so the markup is the manifest: a state
// is only served by its own assets when every attribute that state needs is
// present on the module root. Otherwise the default pair is used, which is how
// consent-pay behaved for states it never declared.

import { loadAgreementTemplate } from "./template-loader"
import { renderAgreementTemplate } from "./template-renderer"

export type AgreementValues = Readonly<Record<string, string>>

export interface AgreementPlan {
  readonly subscription: string
  readonly disclosure?: string
}

export interface StateAgreements {
  readonly subscription: string
  readonly disclosure?: string
}

const DEFAULT_PLAN: AgreementPlan = {
  subscription: "data-default-subscription-template-url",
  disclosure: "data-default-disclosure-template-url",
}

/** Subscription/disclosure asset pair per enrollment state. */
export const STATE_AGREEMENT_PLANS: Readonly<Record<string, AgreementPlan>> = {
  Colorado: {
    subscription: "data-colorado-subscription-template-url",
    disclosure: "data-colorado-disclosure-template-url",
  },
  Delaware: {
    subscription: "data-delaware-subscription-template-url",
    disclosure: "data-default-disclosure-template-url",
  },
  Maine: {
    subscription: "data-maine-subscription-template-url",
    disclosure: "data-maine-disclosure-template-url",
  },
  Maryland: {
    subscription: "data-maryland-subscription-template-url",
    disclosure: "data-default-disclosure-template-url",
  },
  Minnesota: {
    subscription: "data-minnesota-subscription-template-url",
  },
  "New York": {
    subscription: "data-new-york-subscription-template-url",
    disclosure: "data-new-york-disclosure-template-url",
  },
  "Rhode Island": {
    subscription: "data-rhode-island-subscription-template-url",
  },
}

function hasAttributes(root: Element | null, plan: AgreementPlan): boolean {
  const attributes = plan.disclosure ? [plan.subscription, plan.disclosure] : [plan.subscription]
  return attributes.every((attribute) => !!root?.getAttribute(attribute))
}

/** The plan a module can actually serve for `stateName`. */
export function resolveAgreementPlan(root: Element | null, stateName: string | null | undefined): AgreementPlan {
  const plan = stateName ? STATE_AGREEMENT_PLANS[stateName] : undefined
  if (!plan) return DEFAULT_PLAN

  if (!hasAttributes(root, plan)) {
    console.warn(`No agreement assets declared for "${stateName}"; using the default agreements.`)
    return DEFAULT_PLAN
  }

  return plan
}

/** Fetch one agreement asset by data attribute and render its tokens. */
export async function loadAgreement(root: Element | null, attribute: string, values: AgreementValues): Promise<string> {
  const template = await loadAgreementTemplate(root?.getAttribute(attribute) ?? "")
  return renderAgreementTemplate(template, values)
}

/**
 * Fetch the subscription agreement for `stateName` — and its disclosure when
 * that state has one — concurrently. Token values differ per document (states
 * disagree on date formats and on whether the signer or the account holder
 * name is printed), so the caller supplies one map per kind.
 */
export async function loadStateAgreements(
  root: Element | null,
  stateName: string | null | undefined,
  values: {
    readonly subscription: AgreementValues
    readonly disclosure?: AgreementValues
  },
): Promise<StateAgreements> {
  const plan = resolveAgreementPlan(root, stateName)
  const [subscription, disclosure] = await Promise.all([
    loadAgreement(root, plan.subscription, values.subscription),
    plan.disclosure ? loadAgreement(root, plan.disclosure, values.disclosure ?? {}) : Promise.resolve(undefined),
  ])

  return { subscription, disclosure }
}

export interface TemplateGate {
  /** Resolves true once templates are loaded; retries once after a failure. */
  ensureReady(): Promise<boolean>
}

/**
 * Start `load()` immediately and gate every preview/print/download/upload
 * action behind it, so a failed fetch surfaces an error instead of producing a
 * blank PDF. A later action retries a failed load.
 */
export function createTemplateGate(load: () => Promise<unknown>): TemplateGate {
  let loadError: unknown = null
  let pending: Promise<unknown>

  function start(): void {
    loadError = null
    pending = load()
    pending.catch((error) => {
      loadError = error
      console.error("Unable to load the agreement template:", error)
    })
  }

  start()

  return {
    async ensureReady(): Promise<boolean> {
      if (loadError) start()

      try {
        await pending
        return true
      } catch {
        window.alert("We could not load your agreement. Please check your connection and try again.")
        return false
      }
    },
  }
}
